import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { Icon, SearchInput, tokens } from 'components/v3';

// Search (debounced 300 ms; the server searches every answer, the
// submitter and the entry id — T28), the live entry count, and Export,
// which opens the Export dialog (CSV, Excel or a PDF report of the fields
// you pick). v3 SearchInput takes no aria-label, so the field is named by
// its placeholder inside a labelled search landmark.
// Optional (w9-plan §6.3 item 6): `children` render between the count and
// Export (status chips, filter menus); `searchPlaceholder` renames the
// search. Both default to today's toolbar.

export const entryCount = (n) =>
  `${Number(n || 0).toLocaleString()} ${n === 1 ? 'entry' : 'entries'}`;

export const SEARCH_PLACEHOLDER = 'Search entries';

const SubmissionsToolbar = ({
  count,
  counting,
  onSearch,
  onExport,
  canExport,
  isMobile,
  children,
  searchPlaceholder,
}) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: isMobile ? 10 : 14,
      }}
    >
      <div
        role="search"
        aria-label="Search entries"
        data-tour="submissions-search"
        style={{
          display: 'flex',
          flex: isMobile ? '1 1 100%' : '0 1 360px',
          minWidth: 0,
        }}
      >
        <SearchInput
          aria-label={searchPlaceholder}
          placeholder={searchPlaceholder}
          debounce={300}
          onChange={onSearch}
        />
      </div>
      <div
        aria-live="polite"
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: t.sub,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {counting ? ' ' : entryCount(count)}
      </div>
      {children ? (
        <div
          data-fb-toolbar-slot=""
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: isMobile ? 8 : 10,
            flex: isMobile ? '1 1 100%' : '0 1 auto',
            minWidth: 0,
          }}
        >
          {children}
        </div>
      ) : null}
      <div style={{ marginLeft: 'auto' }}>
        <ButtonV2
          primary
          onClick={onExport}
          disabled={!canExport}
          icon={<Icon name="download" size={16} color="currentColor" />}
          data-tour="submissions-export"
        >
          Export
        </ButtonV2>
      </div>
    </div>
  );
};

SubmissionsToolbar.propTypes = {
  count: PropTypes.number,
  counting: PropTypes.bool,
  onSearch: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  canExport: PropTypes.bool,
  isMobile: PropTypes.bool,
  children: PropTypes.node,
  searchPlaceholder: PropTypes.string,
};

SubmissionsToolbar.defaultProps = {
  count: 0,
  counting: false,
  canExport: true,
  isMobile: false,
  children: null,
  searchPlaceholder: SEARCH_PLACEHOLDER,
};

export default SubmissionsToolbar;
